import React from 'react';
import { useUIStore } from '@/store';

interface SettingRowProps {
  label: string;
  description?: string;
  children: React.ReactNode;
}

function SettingRow({ label, description, children }: SettingRowProps) {
  return (
    <div className="flex items-center justify-between py-3 border-b border-[#3e3e42]">
      <div className="flex-1 min-w-0 mr-4">
        <div className="text-sm text-[#cccccc]">{label}</div>
        {description && (
          <div className="text-xs text-[#858585] mt-0.5">{description}</div>
        )}
      </div>
      <div className="shrink-0">{children}</div>
    </div>
  );
}

function SelectInput({
  value,
  options,
  onChange,
}: {
  value: string | number;
  options: { label: string; value: string | number }[];
  onChange: (v: string) => void;
}) {
  return (
    <select
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className="bg-[#3c3c3c] text-[#cccccc] text-xs border border-[#3e3e42] rounded px-2 py-1 outline-none focus:border-[#007acc] min-w-[120px]"
    >
      {options.map((o) => (
        <option key={o.value} value={o.value}>
          {o.label}
        </option>
      ))}
    </select>
  );
}

function Toggle({
  checked,
  onChange,
}: {
  checked: boolean;
  onChange: (v: boolean) => void;
}) {
  return (
    <button
      type="button"
      onClick={() => onChange(!checked)}
      className={`relative w-9 h-5 rounded-full transition-colors ${
        checked ? 'bg-[#007acc]' : 'bg-[#3c3c3c]'
      }`}
    >
      <span
        className={`absolute top-0.5 left-0.5 w-4 h-4 rounded-full bg-white transition-transform ${
          checked ? 'translate-x-4' : ''
        }`}
      />
    </button>
  );
}

export default function SettingsPanel() {
  const {
    showTerminal,
    showPreview,
    sidebarWidth,
    terminalHeight,
    toggleTerminal,
    togglePreview,
    setSidebarWidth,
    setTerminalHeight,
  } = useUIStore();

  // Local settings stored in localStorage
  const [fontSize, setFontSize] = React.useState(() =>
    parseInt(localStorage.getItem('kn-ide-fontSize') || '14', 10)
  );
  const [tabSize, setTabSize] = React.useState(() =>
    parseInt(localStorage.getItem('kn-ide-tabSize') || '2', 10)
  );
  const [wordWrap, setWordWrap] = React.useState(() =>
    localStorage.getItem('kn-ide-wordWrap') !== 'false'
  );
  const [minimap, setMinimap] = React.useState(() =>
    localStorage.getItem('kn-ide-minimap') !== 'false'
  );
  const [autoSave, setAutoSave] = React.useState(() =>
    localStorage.getItem('kn-ide-autoSave') !== 'false'
  );
  const [autoSaveDelay, setAutoSaveDelay] = React.useState(() =>
    parseInt(localStorage.getItem('kn-ide-autoSaveDelay') || '1000', 10)
  );

  const persist = (key: string, val: string) => localStorage.setItem(key, val);

  const handleFontSize = (v: string) => {
    const n = parseInt(v, 10);
    setFontSize(n);
    persist('kn-ide-fontSize', v);
  };

  const handleTabSize = (v: string) => {
    const n = parseInt(v, 10);
    setTabSize(n);
    persist('kn-ide-tabSize', v);
  };

  const handleWordWrap = (v: boolean) => {
    setWordWrap(v);
    persist('kn-ide-wordWrap', String(v));
  };

  const handleMinimap = (v: boolean) => {
    setMinimap(v);
    persist('kn-ide-minimap', String(v));
  };

  const handleAutoSave = (v: boolean) => {
    setAutoSave(v);
    persist('kn-ide-autoSave', String(v));
  };

  const handleAutoSaveDelay = (v: string) => {
    const n = parseInt(v, 10);
    setAutoSaveDelay(n);
    persist('kn-ide-autoSaveDelay', v);
  };

  return (
    <div className="p-4 text-sm">
      {/* Editor Section */}
      <div className="mb-6">
        <h3 className="text-xs font-semibold uppercase tracking-wider text-[#bbbbbb] mb-2">
          Editor
        </h3>

        <SettingRow label="Font Size" description="Editor font size in pixels">
          <SelectInput
            value={fontSize}
            onChange={handleFontSize}
            options={[
              { label: '12px', value: 12 },
              { label: '13px', value: 13 },
              { label: '14px', value: 14 },
              { label: '15px', value: 15 },
              { label: '16px', value: 16 },
              { label: '18px', value: 18 },
              { label: '20px', value: 20 },
            ]}
          />
        </SettingRow>

        <SettingRow label="Tab Size" description="Number of spaces per tab">
          <SelectInput
            value={tabSize}
            onChange={handleTabSize}
            options={[
              { label: '2 spaces', value: 2 },
              { label: '4 spaces', value: 4 },
              { label: '8 spaces', value: 8 },
            ]}
          />
        </SettingRow>

        <SettingRow label="Word Wrap" description="Wrap lines at viewport width">
          <Toggle checked={wordWrap} onChange={handleWordWrap} />
        </SettingRow>

        <SettingRow label="Minimap" description="Show code minimap on the right">
          <Toggle checked={minimap} onChange={handleMinimap} />
        </SettingRow>
      </div>

      {/* File Section */}
      <div className="mb-6">
        <h3 className="text-xs font-semibold uppercase tracking-wider text-[#bbbbbb] mb-2">
          Files
        </h3>

        <SettingRow label="Auto Save" description="Automatically save files after changes">
          <Toggle checked={autoSave} onChange={handleAutoSave} />
        </SettingRow>

        {autoSave && (
          <SettingRow label="Auto Save Delay" description="Delay before auto-saving (ms)">
            <SelectInput
              value={autoSaveDelay}
              onChange={handleAutoSaveDelay}
              options={[
                { label: '500ms', value: 500 },
                { label: '1000ms', value: 1000 },
                { label: '2000ms', value: 2000 },
                { label: '5000ms', value: 5000 },
              ]}
            />
          </SettingRow>
        )}
      </div>

      {/* Layout Section */}
      <div className="mb-6">
        <h3 className="text-xs font-semibold uppercase tracking-wider text-[#bbbbbb] mb-2">
          Layout
        </h3>

        <SettingRow label="Show Terminal" description="Toggle integrated terminal panel">
          <Toggle checked={showTerminal} onChange={() => toggleTerminal()} />
        </SettingRow>

        <SettingRow label="Show Preview" description="Toggle live preview panel">
          <Toggle checked={showPreview} onChange={() => togglePreview()} />
        </SettingRow>

        <SettingRow label="Sidebar Width" description={`${sidebarWidth}px`}>
          <input
            type="range"
            min={180}
            max={500}
            value={sidebarWidth}
            onChange={(e) => setSidebarWidth(parseInt(e.target.value, 10))}
            className="w-24 accent-[#007acc]"
          />
        </SettingRow>

        <SettingRow label="Terminal Height" description={`${terminalHeight}px`}>
          <input
            type="range"
            min={100}
            max={600}
            value={terminalHeight}
            onChange={(e) => setTerminalHeight(parseInt(e.target.value, 10))}
            className="w-24 accent-[#007acc]"
          />
        </SettingRow>
      </div>

      {/* Keyboard Shortcuts Reference */}
      <div>
        <h3 className="text-xs font-semibold uppercase tracking-wider text-[#bbbbbb] mb-2">
          Keyboard Shortcuts
        </h3>
        <div className="space-y-1.5 text-xs text-[#858585]">
          {[
            ['Ctrl+S', 'Save file'],
            ['Ctrl+Shift+P', 'Command palette'],
            ['Ctrl+`', 'Toggle terminal'],
            ['Ctrl+B', 'Toggle sidebar'],
            ['Ctrl+Shift+E', 'Explorer'],
            ['Ctrl+Shift+D', 'Deploy panel'],
          ].map(([key, desc]) => (
            <div key={key} className="flex items-center justify-between">
              <span className="text-[#cccccc]">{desc}</span>
              <kbd className="bg-[#3c3c3c] border border-[#505050] rounded px-1.5 py-0.5 text-[10px] font-mono text-[#cccccc]">
                {key}
              </kbd>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
